/**
 * Synthia Engine 配置加载器 - 简化版
 * 提供基本的配置文件加载和解析功能
 */

import fs from 'fs-extra';
import { resolve, extname } from 'path';
import { merge } from 'lodash-es';
import type { SynthiaConfig, ConfigLoadOptions } from './config';
import { DEFAULT_CONFIG } from './config';
import { ConfigValidator } from './config-validator';

/**
 * 简化的配置加载器类
 */
export class ConfigLoader {
  private rootDir: string;
  private configCache = new Map<string, SynthiaConfig>();

  constructor(rootDir: string = process.cwd()) {
    this.rootDir = rootDir;
  }

  /**
   * 加载配置文件
   */
  async loadConfig(options: ConfigLoadOptions = {}): Promise<SynthiaConfig> {
    const { configFile, mergeDefaults = true, validate = true } = options;

    // 查找配置文件
    const configPath = this.findConfigFile(configFile);

    if (!configPath) {
      throw new Error(
        '未找到配置文件，请确保项目根目录存在 synthia.config.js/ts/json'
      );
    }

    // 检查缓存
    const cacheKey = `${configPath}:${fs.statSync(configPath).mtime.getTime()}`;
    if (this.configCache.has(cacheKey)) {
      return this.configCache.get(cacheKey)!;
    }

    // 加载配置
    const config = await this.loadConfigFile(configPath);

    // 合并默认配置
    if (mergeDefaults) {
      const mergedConfig = merge({}, DEFAULT_CONFIG, config);
      this.configCache.set(cacheKey, mergedConfig);

      // 验证配置
      if (validate) {
        const validator = new ConfigValidator();
        const validation = validator.validate(mergedConfig);
        if (!validation.valid) {
          console.warn('配置验证警告:', validation.warnings.join(', '));
        }
      }

      return mergedConfig;
    }

    this.configCache.set(cacheKey, config);
    return config;
  }

  /**
   * 查找配置文件
   */
  private findConfigFile(configFile?: string): string | null {
    const configFiles = configFile
      ? [configFile]
      : [
          'synthia.config.js',
          'synthia.config.ts',
          'synthia.config.json',
          'synthia.config.mjs',
        ];

    for (const file of configFiles) {
      const fullPath = resolve(this.rootDir, file);
      if (fs.existsSync(fullPath)) {
        return fullPath;
      }
    }

    return null;
  }

  /**
   * 加载配置文件内容
   */
  private async loadConfigFile(configPath: string): Promise<SynthiaConfig> {
    const ext = extname(configPath);

    try {
      if (ext === '.json') {
        const content = fs.readFileSync(configPath, 'utf-8');
        return JSON.parse(content);
      } else if (ext === '.js' || ext === '.mjs') {
        // 动态导入 JS 文件
        const module = await import(configPath);
        return module.default || module;
      } else if (ext === '.ts') {
        // 动态导入 TS 文件
        const module = await import(configPath);
        return module.default || module;
      } else {
        throw new Error(`不支持的配置文件格式: ${ext}`);
      }
    } catch (error) {
      throw new Error(`加载配置文件失败: ${error}`);
    }
  }

  /**
   * 清除配置缓存
   */
  clearCache(): void {
    this.configCache.clear();
  }

  /**
   * 获取缓存大小
   */
  getCacheSize(): number {
    return this.configCache.size;
  }
}
